import {
    createPagePagingActions,
    Field,
    FinalFormInput,
    SortDirection,
    StackSwitchApiContext,
    Table as AdminTable,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
    VisibleType,
} from "@comet/admin";
import { Grid, IconButton } from "@mui/material";
import { ContentWrapper, formatDateTimeAsString, FormattedDateWidthDateTime, Status, useMemoizedTableVariables } from "@src/components";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import { ResetButton } from "@src/components/ResetButton";
import { StyledFilter, StyledSearch, TableFilter } from "@src/components/table/TableStyles";
import { type GQLCodesListAdminQuery, type GQLCodesListAdminQueryVariables } from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FunctionComponent, type PropsWithChildren, useCallback, useContext } from "react";
import { useIntl } from "react-intl";

import { codesListQuery } from "./Table.gql";

interface FilterVariables {
    query?: string;
    activated?: boolean;
}

interface TableProps {
    persistedStateId?: string;
}

const Table: FunctionComponent<PropsWithChildren<TableProps>> = ({ persistedStateId }) => {
    const pagingApi = useTableQueryPaging(1, { persistedStateId });
    const sortApi = useTableQuerySort({ columnName: "licence.createdAt", direction: SortDirection.DESC }, { persistedStateId });
    const stackApi = useContext(StackSwitchApiContext);
    const filterApi = useTableQueryFilter<FilterVariables>({}, { pagingApi, persistedStateId });
    const addAdditionalQueryVariablesFn = useCallback(
        () => ({ activated: filterApi.current.activated, query: filterApi.current.query }),
        [filterApi],
    );

    const intl = useIntl();

    const memoizedVariables = useMemoizedTableVariables<GQLCodesListAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, api, loading, error } = useTableQuery<GQLCodesListAdminQuery, GQLCodesListAdminQueryVariables>()(codesListQuery, {
        resolveTableData: ({ licencesAdmin }) => ({
            data: licencesAdmin.nodes,
            totalCount: licencesAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, licencesAdmin),
        }),
        variables: {
            ...memoizedVariables,
        },
    });

    const activatedOptions = [
        {
            label: intl.formatMessage({ id: "reviermanager.codes.filter.activated.true", defaultMessage: "Ja" }),
            value: true,
        },
        {
            label: intl.formatMessage({ id: "reviermanager.purchases.filter.activated.false", defaultMessage: "Nein" }),
            value: false,
        },
    ];

    const handleFilterReset = () => {
        filterApi.formApi.reset({});
    };

    const fileName = `${intl.formatMessage({
        id: "reviermanager.common.export.licences",
        defaultMessage: "Codes",
    })}`;

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    return (
        <ContentWrapper marginVariant="table">
            <TableFilterFinalForm filterApi={filterApi}>
                <TableFilter container>
                    <Grid size={2}>
                        <StyledFilter>
                            <StaticSelectField
                                name="activated"
                                label={intl.formatMessage({
                                    id: "reviermanager.codes.filter.activated",
                                    defaultMessage: "eingelöst?",
                                })}
                                options={activatedOptions}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                                isClearable
                                variant="vertical"
                                searchable={false}
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={3}>
                        <StyledFilter>
                            <Field
                                type="text"
                                label={intl.formatMessage({ id: "reviermanager.codes.filter.fulltext", defaultMessage: "Suche" })}
                                name="query"
                                component={FinalFormInput}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.codes.filter.fulltext.placeholder",
                                    defaultMessage: "Code, UUID, ...",
                                })}
                                startAdornment={<StyledSearch />}
                                variant="vertical"
                                fullWidth
                            />
                        </StyledFilter>
                    </Grid>
                    {Object.keys(filterApi.current).length > 0 && (
                        <Grid>
                            <ResetButton onClick={handleFilterReset} />
                        </Grid>
                    )}
                </TableFilter>
            </TableFilterFinalForm>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        exportApis={[exportApi]}
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.codes.table",
                                defaultMessage: "{count, plural, one {Code} other {Codes}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "licence.createdAt",
                                header: intl.formatMessage({ id: "reviermanager.common.codes.created", defaultMessage: "erstellt am" }),
                                cellProps: { size: "medium" },
                                render: ({ createdAt }) => <FormattedDateWidthDateTime value={createdAt} />,
                                sortable: true,
                                renderExcel: ({ createdAt }) => formatDateTimeAsString(createdAt, intl),
                            },
                            {
                                name: "licence.code",
                                header: intl.formatMessage({ id: "reviermanager.common.codes.code", defaultMessage: "Code" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ code }) => code,
                            },
                            {
                                name: "package.name",
                                header: intl.formatMessage({ id: "reviermanager.common.codes.package.name", defaultMessage: "Paket" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                            },
                            {
                                name: "id",
                                header: intl.formatMessage({ id: "reviermanager.common.codes.id", defaultMessage: "UUID" }),
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                            },
                            {
                                name: "type",
                                header: intl.formatMessage({ id: "reviermanager.common.codes.type", defaultMessage: "Typ" }),
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                            },
                            {
                                name: "licence.activatedAt",
                                header: intl.formatMessage({ id: "reviermanager.common.codes.activated", defaultMessage: "eingelöst?" }),
                                cellProps: { size: "medium" },
                                render: ({ activatedAt }) => <Status isActivated={activatedAt} />,
                                renderExcel: ({ activatedAt }) =>
                                    activatedAt
                                        ? intl.formatMessage({ id: "reviermanager.common.yes", defaultMessage: "Ja" })
                                        : intl.formatMessage({ id: "reviermanager.common.no", defaultMessage: "Nein" }),
                            },
                            {
                                name: "licence.activeFrom",
                                header: intl.formatMessage({
                                    id: "reviermanager.common.codes.activeFrom",
                                    defaultMessage: "Lizenz aktiviert am",
                                }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ activeFrom }) => activeFrom && <FormattedDateWidthDateTime value={activeFrom} />,
                                renderExcel: ({ activeFrom }) => formatDateTimeAsString(activeFrom, intl),
                            },
                            {
                                name: "licence.activeTill",
                                header: intl.formatMessage({ id: "reviermanager.common.codes.activeTill", defaultMessage: "Lizenz läuft ab am" }),
                                cellProps: { size: "medium" },
                                sortable: true,
                                render: ({ activeTill }) => activeTill && <FormattedDateWidthDateTime value={activeTill} />,
                                renderExcel: ({ activeTill }) => formatDateTimeAsString(activeTill, intl),
                            },
                            {
                                name: "actions",
                                visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                render: ({ id }) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            stackApi.activatePage("information", String(id));
                                        }}
                                    >
                                        <ArrowRight color="primary" />
                                    </IconButton>
                                ),
                            },
                        ]}
                    />
                )}
            </TableQuery>
            {tableData && <CSVExportButton exportApi={exportApi} />}
        </ContentWrapper>
    );
};

export default Table;
